/**
 * Small session/context readers derived from shared state (the current demand,
 * the requester role, the org+demand pair for mutations). State-only leaf so
 * loaders, screens, and the shell can all import it without a cycle (app.tsx
 * split, HS2-ZB7GJN).
 */
import { isAssignableRoles } from '../../shared/demand-assignee.js';
import { demandAreaEditable, type DemandEditArea, isLockableStatus } from '../../shared/demand-editability.js';
import { canReopenWithUpdate, managesDemands } from '../../shared/demand-transitions.js';
import { me, workspace } from '../data/state.js';
import { actingReadOnly } from './acting-read-only.js';

export type PrimaryView = 'demands' | 'chat' | 'stats' | 'settings';

/** A settings page reachable from the settings sidebar. */
export type SettingsTab = 'organization' | 'project' | 'personal';

export interface RoleCapabilities {
  primaryViews: PrimaryView[];
  commercialDemand: boolean;
  organizationSettings: boolean;
}

export type DemandSegment = 'info' | 'lines' | 'quotes' | 'files' | 'summary' | 'decision-summary';

/** Translate membership roles into the client surfaces that may be rendered.
 * Server authorization remains authoritative; this keeps unavailable controls
 * and their data requests out of the UI rather than presenting dead actions. */
export function capabilitiesForRoles(roles: string[]): RoleCapabilities {
  const staff = roles.some((role) => role === 'procurement' || role === 'reviewer' || role === 'admin');
  return {
    // Settings is a primary view for every role (HS2-CJM2YK); a requester only
    // reaches its Personal page.
    primaryViews: staff ? ['demands', 'chat', 'stats', 'settings'] : ['demands', 'settings'],
    commercialDemand: staff,
    organizationSettings: staff,
  };
}

/** The settings page to open when Settings is chosen: the remembered one when the
 *  role may see it, otherwise Personal (Preferences), the default (HS2-CJM2YK). */
export function settingsTabForCapabilities(
  remembered: string | undefined,
  capabilities: RoleCapabilities,
): SettingsTab {
  if (remembered === 'personal') return 'personal';
  if ((remembered === 'organization' || remembered === 'project') && capabilities.organizationSettings)
    return remembered;
  return 'personal';
}

export function currentCapabilities(): RoleCapabilities {
  return capabilitiesForRoles(me.value?.roles ?? []);
}

/** The id of the demand currently open in the workspace/editor, if any. */
export function currentDemandId(): string | undefined {
  const d = workspace.value?.demand as { id?: string } | undefined;
  return d?.id;
}

/** The { orgId, demandId } pair for a mutation on the open demand, or null. */
export function intakeCtx(): { orgId: string; demandId: string } | null {
  const orgId = me.value?.organizationId;
  const demandId = currentDemandId();
  if (orgId === null || orgId === undefined || demandId === undefined) return null;
  return { orgId, demandId };
}

/** True when the signed-in member holds only the requester role (price-free UI). */
/** Whether the signed-in member administers the organization (settings writes). */
export function isOrgAdmin(): boolean {
  return me.value?.roles.includes('admin') === true;
}

export function isRequester(): boolean {
  return !currentCapabilities().commercialDemand;
}

export function demandSegmentsForCapabilities(capabilities: RoleCapabilities): DemandSegment[] {
  return capabilities.commercialDemand
    ? ['info', 'lines', 'quotes', 'files', 'summary', 'decision-summary']
    : ['info', 'lines', 'files'];
}

export function canOpenDemandSegment(segment: DemandSegment): boolean {
  return demandSegmentsForCapabilities(currentCapabilities()).includes(segment);
}

export function demandIsCancelled(): boolean {
  const d = workspace.value?.demand as { status?: string } | undefined;
  return d?.status === 'cancelled';
}

function openDemandState(): { status: string; reopened: boolean } {
  const d = workspace.value?.demand as { status?: string; changesReopenedAt?: string | null } | undefined;
  return { status: d?.status ?? '', reopened: (d?.changesReopenedAt ?? null) !== null };
}

/** Whether `area` of the open demand is locked by its status (HS2-Y50H8E), regardless of role. */
export function demandAreaLocked(area: DemandEditArea): boolean {
  const { status, reopened } = openDemandState();
  return !demandAreaEditable(status, area, reopened);
}

/** A requester follows a submitted demand read-only; they edit only their draft or a returned one. */
function requesterFollowing(): boolean {
  const { status } = openDemandState();
  return isRequester() && status !== 'draft' && status !== 'returned';
}

/** True when the open demand's contents (lines, quotes, documents, analysis) cannot be edited.
 * Cancelled and closed demands are read-only for everyone, a decided one locks its contents until
 * reopened for changes (HS2-Y50H8E), and requesters can edit their draft and follow the submitted
 * demand in the same editor without gaining procurement controls. */
export function demandReadOnly(): boolean {
  return actingReadOnly() || demandAreaLocked('contents') || requesterFollowing();
}

/** True when the open demand's details (title, description, needed-by, notes) cannot be edited:
 * still open while decided, locked once closed or cancelled (HS2-Y50H8E). */
export function demandDetailsReadOnly(): boolean {
  return actingReadOnly() || demandAreaLocked('details') || requesterFollowing();
}

/** True when the open demand's line decisions and outcomes are locked (closed or cancelled). */
export function demandDecisionsLocked(): boolean {
  return actingReadOnly() || demandAreaLocked('decisions');
}

/** The lock banner a staff member sees on a decided or closed demand (HS2-Y50H8E). */
export interface DemandLockState {
  state: 'decided' | 'closed' | 'reopened';
  tone: 'info' | 'warning';
  title: string;
  detail: string;
  action: { name: 'demand-reopen-for-changes' | 'demand-lock-changes'; label: string } | null;
}

/** The open demand's lock banner, or null when its status does not lock it. */
export function demandLockState(manager: boolean): DemandLockState | null {
  const { status, reopened } = openDemandState();
  // A rejected demand's banner is the rejection one, with Reopen with Update (HS2-Z6MFSA).
  if (!isLockableStatus(status) || (status === 'rejected' && !reopened)) return null;
  if (reopened)
    return {
      state: 'reopened',
      tone: 'warning',
      title: 'Reopened for changes.',
      detail: `Everything can be edited until this demand is ${status === 'decided' ? 'decided' : status === 'rejected' ? 'rejected' : 'complete'} again or it is locked.`,
      action: manager && !actingReadOnly() ? { name: 'demand-lock-changes', label: 'Lock' } : null,
    };
  const reopen =
    manager && !actingReadOnly() ? ({ name: 'demand-reopen-for-changes', label: 'Reopen for Changes' } as const) : null;
  return status === 'closed'
    ? { state: 'closed', tone: 'info', title: 'This demand is complete.', detail: 'It is read-only.', action: reopen }
    : {
        state: 'decided',
        tone: 'info',
        title: 'This demand is decided.',
        detail: 'Line items, quotes, and documents are locked. Decisions and outcomes can still change.',
        action: reopen,
      };
}

/** Whether the member may set or clear a demand's assignee: an active staff member (FR-5.20, HS2-C63T9N). */
export function canAssignDemands(): boolean {
  const current = me.value;
  return current !== null && !current.suspended && !actingReadOnly() && isAssignableRoles(current.roles);
}

/** Whether the member may create (and so duplicate) demands: requesters, reviewers, and admins (HS2-Q2NPBT). */
export function canCreateDemands(): boolean {
  return (
    !actingReadOnly() &&
    (me.value?.roles ?? []).some((role) => role === 'requester' || role === 'reviewer' || role === 'admin')
  );
}

/**
 * True when the open demand's notes cannot take new posts: only once its details lock (closed or
 * cancelled). Unlike other details, a requester can post notes on a demand they submitted, so they
 * can answer and add clarifications (HS2-E33XQD).
 */
export function demandNotesReadOnly(): boolean {
  return actingReadOnly() || demandAreaLocked('details');
}

/** The open demand's current rejected lines and whether this member may reopen it with an update (HS2-Z6MFSA). */
export function demandRejectionState(): { rejected: number; total: number; canReopen: boolean } | null {
  const ws = workspace.value;
  if (ws === null) return null;
  const demand = ws.demand as { status?: string; requesterMemberId?: string | null } | undefined;
  const status = demand?.status ?? '';
  if (status !== 'rejected' && status !== 'closed') return null;
  const rejected = ((ws.decisions ?? []) as { isCurrent?: boolean; action?: string | null }[]).filter(
    (decision) => decision.isCurrent === true && decision.action === 'reject',
  ).length;
  if (rejected === 0) return null;
  const roles = me.value?.roles ?? [];
  const ownRequester =
    roles.includes('requester') && (demand?.requesterMemberId ?? null) === (me.value?.memberId ?? '');
  return {
    rejected,
    total: ((ws.lineItems ?? []) as unknown[]).length,
    canReopen: !actingReadOnly() && canReopenWithUpdate(status, rejected, managesDemands(roles), ownRequester),
  };
}
